import React from 'react';
import {useWindowDimensions, View} from 'react-native';
import {get} from 'lodash';

import Text from '/component/base/Text';
import {IncentiveTrip} from '/constant/TestIDs';
import {withCompetency, withTheme} from '/provider/ThemeProvider';
import {useTranslation} from '/provider/TranslationProvider';
import {withHOC} from '/util/hoc.util';
import testProperties from '/util/testing/testProperties';

import {CoachRank} from './Requirements/CoachRank';
import {FrontlineQualifyingVolume} from './Requirements/FrontlineQualifyingVolume';
import {NewClients} from './Requirements/NewClients';
import {NewCoach} from './Requirements/NewCoach';
import {SupportPath} from './Requirements/SupportPath';
import {FullyQualifiedBanner} from './FullyQualifiedBanner';
import {styles} from './IncentiveTripBanner.style';
import {IncentiveTripBannerProps} from './IncentiveTripBanner.types';
import {
  generateOnLearnMore,
  getDaysLeftForBonus,
} from './IncentiveTripBanner.util';

export const IncentiveTripBanner = ({
  popoverShow = () => {},
  currentScreen = '',
  endDate = '',
  cardNumber = 0,
  totalCards = 0,
  getTheme = (key: string) => key,
  path = 0,
  coachGoalRank = {},
  orderingEntities = {},
  rankAchievements = [],
  fqv = {currentVolume: '', targetVolume: ''},
  currentDate = '',
  newCoachCount = {},
  supportPathCount = {},
  handleBannerHeight,
  bannerHeight = 0,
  isQualified = false,
  featureFlag = {showIncentiveUpdate: false},
}: IncentiveTripBannerProps) => {
  const {fontScale} = useWindowDimensions();
  const isLargeFont = fontScale > 1.15;
  const iconColor = getTheme('competency.backgroundColor.backgroundColor');
  const {tt, ts} = useTranslation('incentiveTripBanner');

  const daysLeftForBonus = getDaysLeftForBonus(currentDate, endDate);
  const showIncentiveUpdate = get(featureFlag, 'showIncentiveUpdate', false);

  const onLearnMoreClick = () =>
    generateOnLearnMore({popoverShow, currentScreen});

  const renderDescription = () => {
    return tt('content', () => (
      <Text
        allowFontScaling
        maxFontSizeMultiplier={2}
        {...testProperties(IncentiveTrip.itDescription)}>
        Qualify for this year’s rewards trip by meeting the following
        requirements. Your requirements (or path) are determined by your
        performing rank in April 2023.{' '}
        {tt('learnMore', () => (
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            onPress={onLearnMoreClick()}
            style={styles.link}
            {...testProperties(IncentiveTrip.itLearnMore)}>
            Learn more
          </Text>
        ))}
      </Text>
    ));
  };

  return (
    <>
      {isQualified ? (
        <FullyQualifiedBanner
          totalCards={totalCards}
          cardNumber={cardNumber}
          path={path}
          onLearnMoreClick={onLearnMoreClick}
          // @ts-expect-error TS(2322): Type '{ totalCards: any; cardNumber: any; path: an... Remove this comment to see the full error message
          bannerHeight={bannerHeight}
          handleBannerHeight={handleBannerHeight}
          style={{banner: styles.incentiveTripContent}}
        />
      ) : (
        <View
          style={{
            ...styles.incentiveTripContent,
            ...(bannerHeight ? {minHeight: bannerHeight} : {}),
          }}
          onLayout={handleBannerHeight}
          {...testProperties(IncentiveTrip.itBanner)}>
          <View
            style={[
              styles.headerContainer,
              isLargeFont && styles.headerContainerLargeFont,
            ]}
            {...testProperties(IncentiveTrip.itHeaderContainer)}>
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              variant="heading"
              style={styles.headerText}
              {...testProperties(IncentiveTrip.itTitle)}>
              {tt('heading', () => (
                <>
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    style={styles.optaText}>
                    OPTA
                  </Text>
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    style={styles.viaText}>
                    VIA
                  </Text>
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    style={styles.titleText}>
                    {' '}
                    Incentive Trip 2024
                  </Text>
                </>
              ))}
            </Text>
            {totalCards &&
              cardNumber &&
              tt('sideTitle', () => (
                <Text
                  allowFontScaling
                  maxFontSizeMultiplier={2}
                  variant="button"
                  style={[
                    styles.sideText,
                    isLargeFont && styles.sideTextLargeFont,
                  ]}
                  {...testProperties(IncentiveTrip.itSideTitle)}>
                  {cardNumber} of {totalCards}
                </Text>
              ))}
          </View>
          {renderDescription()}
          <View
            style={styles.divider}
            {...testProperties(IncentiveTrip.itDivider)}
          />
          <View
            style={styles.heading}
            {...testProperties(IncentiveTrip.itHeadingContainer)}>
            <View
              style={styles.subHeading}
              {...testProperties(IncentiveTrip.itSubHeadingContainer)}>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                variant="heading"
                style={styles.requirements}
                {...testProperties(IncentiveTrip.itRequirementsLabel)}>
                {ts('requirements', 'Requirements')}
              </Text>
              {tt('path', () => (
                <Text
                  allowFontScaling
                  maxFontSizeMultiplier={2}
                  variant="button"
                  emphasis="medium"
                  style={styles.path}
                  {...testProperties(IncentiveTrip.itPathLabel)}>
                  Path {path}
                </Text>
              ))}
              {tt('daysLeft', () => (
                <Text
                  allowFontScaling
                  maxFontSizeMultiplier={2}
                  style={styles.daysLeft}
                  emphasis="medium"
                  variant="button"
                  {...testProperties(IncentiveTrip.itDaysLeft)}>
                  {daysLeftForBonus}{' '}
                  {Number(daysLeftForBonus) === 1 ? 'day' : 'days'} left to
                  qualify
                </Text>
              ))}
            </View>
          </View>
          <CoachRank
            iconColor={iconColor}
            rankAchievements={rankAchievements}
            coachGoalRank={coachGoalRank}
            path={path}
            currentDate={currentDate}
            showIncentiveUpdate={showIncentiveUpdate}
          />
          {path !== 5 && (
            <NewClients
              iconColor={iconColor}
              orderingEntities={orderingEntities}
            />
          )}
          {path === 1 && !showIncentiveUpdate && (
            <FrontlineQualifyingVolume iconColor={iconColor} fqv={fqv} />
          )}
          {path !== 5 && !showIncentiveUpdate && (
            <NewCoach iconColor={iconColor} newCoachCount={newCoachCount} />
          )}
          {(path === 4 || path === 5) && (
            <SupportPath
              iconColor={iconColor}
              supportPathCount={supportPathCount}
            />
          )}
        </View>
      )}
    </>
  );
};

export const IncentiveTripBannerConnected = withHOC(
  withCompetency,
  withTheme,
)(IncentiveTripBanner);
